/**
 * What a tool panel is handed.
 *
 * ONE context object rather than a growing prop list per panel: every panel
 * takes the same shape, so the dispatcher in `index.tsx` can render any tool
 * without knowing what that tool does, and adding a tool never means threading
 * three new props through TableOfContents and both of its call sites.
 *
 * `config` is the tool's saved settings from `AppModule.toolConfigs`, already
 * merged with the registry defaults. Panels read it rather than hard-coding —
 * that link is the whole point of the Tool Library asking the questions.
 */

import type { AppModule, BasemapId, HeatmapConfig, Layer, LocalRasterData } from '../../../types';
import type { Waypoint } from './NavigatePanels';

/* Re-exported, not redeclared. The basemap is saved on the module now, so the
   domain type owns it and a second copy here could drift from what is stored. */
export type { BasemapId };

/** A point the user picked on the map, in WGS84. */
export interface MapPoint {
  lat: number;
  lng: number;
}

/**
 * One row of the app-wide selection, as the tools publish and read it.
 *
 * A description of what is already flowing through `onSelectionChange`, not a
 * new shape: the box-select path, the Query tool and the attribute table all
 * put these fields on their rows. Written down here so a panel can COMBINE
 * with an existing selection without guessing at the members it needs.
 *
 * `key` is the identity — see utils/featureKey. Two reads of the same service
 * return the same feature at different positions, so nothing may be matched by
 * index.
 */
export interface SelectionRow extends Record<string, unknown> {
  id?: string;
  key?: string;
  /** Which layer the feature came from, so results can be grouped. */
  layerId?: string;
  properties?: Record<string, unknown>;
  geometry?: GeoJSON.Geometry;
}

export interface ToolContext {
  /** Saved settings for THIS tool, defaults already applied. */
  config: Record<string, unknown>;

  /* ── The map and its data ── */
  layers: Layer[];
  module: AppModule;
  geoServerUrl: string;
  arcgisUrl: string;
  mapCenter: [number, number];
  mapZoom: number;
  basemap: BasemapId;
  /** Last point clicked on the map, or null before the first click. */
  pointer: MapPoint | null;

  /* ── Things a panel can do ── */
  onGoToLocation: (center: [number, number], zoom: number) => void;
  onToggleLayer: (layerId: string) => void;
  onZoomToLayer: (layer: Layer) => void;
  onLayerChange?: (layerId: string, changes: Partial<Layer>) => void;
  onOpenAttributeTable?: (layer: Layer) => void;
  onBasemapChange?: (basemap: BasemapId) => void;
  /** Opens the Add layers dialog. Absent in the read-only viewer. */
  onAddData?: () => void;
  /** Draws a fleeting overlay (a direction line) that vanishes with the tool. */
  onOverlayChange?: (overlay: GeoJSON.FeatureCollection | null) => void;
  /**
   * Puts a result into the TOC as this person's own temporary layer.
   *
   * Distinct from `onOverlayChange`: an overlay disappears when the panel
   * closes, while this stays until it is removed by hand — and is never saved
   * into the shared module. Returns the new layer's id.
   */
  onCreateTempLayer?: (
    name: string,
    data: GeoJSON.FeatureCollection,
    source: string,
    renderer?: Layer['renderer'],
    /** Density tool only — draws as a heatmap instead of `renderer` above. */
    heatmap?: HeatmapConfig,
  ) => string;
  /** The Georeference tool's own raster twin of `onCreateTempLayer` above —
   *  the only way a panel can add a brand-new RASTER layer; `onCreateTempLayer`
   *  only ever takes a vector `FeatureCollection`. */
  onCreateTempRasterLayer?: (
    name: string,
    raster: LocalRasterData,
    bounds: [number, number, number, number],
    source: string,
  ) => string;
  /** Which layer is clipped by the swipe divider, and which way it splits. */
  onSwipeChange?: (swipe: { layerId: string | null; vertical: boolean }) => void;
  swipeLayerId?: string | null;
  swipeVertical?: boolean;
  /**
   * Publishes what this tool picked out, as the app-wide selection.
   *
   * The same channel the Query tool and the attribute table use, so a trace
   * is highlighted in cyan and ticked in an open table without the panel
   * knowing anything about either.
   */
  onSelectionChange?: (rows: Record<string, unknown>[]) => void;
  /**
   * Frames whatever `onSelectionChange` just published, the same "zoom and
   * blink" every other selection source in this app gets. A panel calls both
   * together — selecting without this leaves a result highlighted wherever
   * the map already happened to be pointed.
   */
  onZoomToSelection?: (rows: Record<string, unknown>[]) => void;
  /**
   * Reads the current app-wide selection.
   *
   * The counterpart to `onSelectionChange`, and the reason Select By Location
   * can offer "add to" and "remove from" the way a desktop GIS does. A
   * FUNCTION rather than a value because the selection changes without this
   * context being rebuilt — a panel that closed over a stale array would
   * quietly combine with whatever was selected when it opened.
   */
  getSelection?: () => SelectionRow[];
  /** Closes this tool's tab. */
  onClose: () => void;

  /**
   * Arms a sketch on the map — the Edit tool's "New feature" flow, a fourth
   * consumer of the same shared sketch controller Draw/Measure/Select
   * already use. 'circle'/'rectangle' finish in two clicks with no
   * vertex-by-vertex placement; 'freehand-polygon'/'freehand-line' trace
   * continuously while the pointer is held down. All five still resolve to
   * a plain Point/LineString/Polygon geometry by the time it comes back
   * through `sketchedFeature`, not a callback, because this context is
   * rebuilt often and a callback captured at call time would go stale.
   */
  onStartFeatureSketch?: (
    shape: 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehand-polygon' | 'freehand-line',
  ) => void;
  /** Abandons a sketch armed by `onStartFeatureSketch`. */
  onCancelFeatureSketch?: () => void;
  /** Closes a line/polygon sketch early — the panel's own Finish button. */
  onFinishFeatureSketch?: () => void;
  /** Vertices placed so far in a sketch armed by `onStartFeatureSketch`. */
  sketchVertexCount?: number;
  /** The shape `onStartFeatureSketch` produced, tagged so the same shape
   *  finished twice still counts twice. */
  sketchedFeature?: { token: number; feature: GeoJSON.Feature } | null;
  /**
   * Shows the Edit tool's open record's own geometry on the map as a
   * highlighted, dashed, non-interactive preview — a brand-new sketch, the
   * feature being reshaped (Esri's own Reshape Feature tool, not
   * vertex-dragging: the admin sketches a brand new path across the part
   * they want to change, armed via the SAME
   * `onStartFeatureSketch('line')`/`sketchedFeature` channel a new feature
   * uses, not a dedicated result channel — `utils/reshapeGeometry.ts`
   * splices it into the span of the original line/polygon nearest the
   * sketch's own two ends), or a feature mid-move would otherwise have no
   * on-map representation at all until Save. Pass null to take the preview
   * back off.
   */
  onDraftFeature?: (geometry: GeoJSON.Geometry | null) => void;
  /**
   * Puts a feature's geometry on the map as a single draggable whole — the
   * Edit tool's "Move" flow. Unlike Reshape, the shape never changes, only
   * its position does. Pass null to take it back off the map.
   */
  onMoveFeature?: (geometry: GeoJSON.Geometry | null) => void;
  /** The geometry after a move-drag finished, tagged the same way as `sketchedFeature`. */
  movedFeature?: { token: number; geometry: GeoJSON.Geometry } | null;

  /**
   * Whether sketching/reshaping snaps to vertices and edges of the vector
   * layers already on the map — the Edit tool's own toggle, but read by the
   * map itself (it drives an ol/interaction/Snap, not anything panel-side).
   */
  snapEnabled?: boolean;
  onSnapEnabledChange?: (enabled: boolean) => void;

  /** False in the viewer, where a normal user may not restructure the map. */
  canManageLayers: boolean;

  /**
   * A From/To pair for DirectionsPanel to pick up the moment it mounts —
   * Near Me's own "Directions from here" button on a result, which needs to
   * both open the Directions tool AND hand it a starting pair, neither of
   * which a panel built from pure local state otherwise has a way to
   * receive. Tokened the same way `sketchedFeature`/`movedFeature` are: the
   * VALUE alone cannot signal "seed again" if a second button press sends
   * the exact same pair, so the token is what the receiving effect keys on.
   */
  directionsSeed?: { token: number; from: Waypoint; to: Waypoint } | null;

  /**
   * The signed-in user's own `data:edit`/`data:edit-attributes` grants —
   * computed once via `can()` where this context is built (App.tsx's
   * `toolContextFor`), not derived per-panel, so every consumer agrees on
   * the same answer. `canEditGeometry` is a strict superset: anyone with it
   * can also do everything `canEditAttributes` allows. See EditPanels.tsx's
   * `EditPanel` for where these actually gate the New-feature/Reshape/
   * Delete affordances apart from plain attribute editing.
   */
  canEditGeometry?: boolean;
  canEditAttributes?: boolean;
}

/** Reads a config value with a fallback, so a panel never renders `undefined`. */
export function cfg<T>(config: Record<string, unknown>, key: string, fallback: T): T {
  const value = config[key];
  return value === undefined || value === null || value === '' ? fallback : (value as T);
}

/**
 * The layers a tool is scoped to.
 *
 * Esri's convention, and the one the Tool Library's help text promises: an
 * empty selection means EVERY layer, not "no layers". A tool configured before
 * any layer existed must not silently stop working once layers are added.
 */
export function scopedLayers(config: Record<string, unknown>, layers: Layer[]): Layer[] {
  const chosen = Array.isArray(config.layers) ? (config.layers as string[]) : [];
  if (chosen.length === 0) return layers;
  const scoped = layers.filter((layer) => chosen.includes(layer.id));
  return scoped.length > 0 ? scoped : layers;
}
